import Badge from "@/components/core/badge";
import type { SalesContract } from "@/features/dashboard/types/sales-contract";
import { formatNumber } from "@/utils/format-number";
import { format } from "date-fns";
import type React from "react";

interface ContractDetailsModalProps {
	contract: SalesContract;
}

export const ContractDetailsModal: React.FC<ContractDetailsModalProps> = ({ contract }) => {
	const getStatusBadge = (status: string) => {
		const colorMap: Record<string, string> = {
			active: "green",
			expired: "red",
			cancelled: "yellow",
		};
		return <Badge color={colorMap[status] || "default"}>{status}</Badge>;
	};

	return (
		<div className="space-y-4">
			<div className="grid grid-cols-2 gap-4">
				<div>
					<h3 className="text-sm font-medium text-gray-500">Project Name</h3>
					<p className="mt-1 text-lg font-semibold">{contract.project_name}</p>
				</div>
				<div>
					<h3 className="text-sm font-medium text-gray-500">Status</h3>
					<div className="mt-1">{getStatusBadge(contract.status)}</div>
				</div>
			</div>

			<div className="grid grid-cols-2 gap-4">
				<div>
					<h3 className="text-sm font-medium text-gray-500">Contract Amount</h3>
					<p className="mt-1 text-lg font-semibold">₱{formatNumber(contract.contract_amount)}</p>
				</div>
				<div>
					<h3 className="text-sm font-medium text-gray-500">Monthly Recurring</h3>
					<p className="mt-1 text-lg font-semibold">₱{formatNumber(contract.monthly_recurring_sales)}</p>
				</div>
			</div>

			<div className="grid grid-cols-2 gap-4">
				<div>
					<h3 className="text-sm font-medium text-gray-500">Start Date</h3>
					<p className="mt-1">{format(new Date(contract.start_date), "PPP")}</p>
				</div>
				<div>
					<h3 className="text-sm font-medium text-gray-500">End Date</h3>
					<p className="mt-1">
						{format(new Date(contract.end_date), "PPP")}
						{contract.is_extended && (
							<Badge color="yellow" className="ml-2">
								Extended
							</Badge>
						)}
					</p>
				</div>
			</div>

			{contract.client_name && (
				<div className="border-t pt-4">
					<h3 className="text-sm font-medium text-gray-500 mb-2">Client Information</h3>
					<div className="space-y-1">
						<p>
							<span className="font-medium">Name:</span> {contract.client_name}
						</p>
						{contract.client_email && (
							<p>
								<span className="font-medium">Email:</span> {contract.client_email}
							</p>
						)}
						{contract.client_phone && (
							<p>
								<span className="font-medium">Phone:</span> {contract.client_phone}
							</p>
						)}
					</div>
				</div>
			)}

			<div className="border-t pt-4">
				<h3 className="text-sm font-medium text-gray-500 mb-2">Salesperson</h3>
				<p>
					{contract.salesperson.name} ({contract.salesperson.email})
				</p>
			</div>

			{contract.notes && (
				<div className="border-t pt-4">
					<h3 className="text-sm font-medium text-gray-500 mb-2">Notes</h3>
					<p className="text-gray-700">{contract.notes}</p>
				</div>
			)}
		</div>
	);
};
